/**
 * Randevu İşlemleri - Mobil Uygulama Akışını Web Üzerinden Simüle Etme
 * Geliştirme/test için: teknisyen seçimi → randevu listesi → işleme başla → işlemler/parçalar/ilave işlemler → tamamla → OTP
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useBackRoute } from '../hooks/useBackRoute';
import { sanitizeString } from '../utils/security';
import IntegerInput from '../components/IntegerInput';
import { parseAdet } from '../utils/integerInput';
import { fetchUserProfile } from '../services/authService';
import {
  fetchAppointmentsByTechService,
  fetchAppointmentDetail,
  fetchAppointmentDetailed,
  updateAppointmentStatus,
  addAppointmentOperation,
  addAppointmentReplacedPart,
  addAppointmentIlaveIslem,
  finishAppointment,
  cancelAppointment,
} from '../services/serviceRecordsService';
import { fetchTechniciansByTechService } from '../services/techniciansService';
import { fetchMobileServiceContents, generateOtp, verifyOtp, completeDeferred } from '../services/mobileAppointmentService';
import { createDevice } from '../services/devicesService';
import MapPickerModal from '../components/MapPickerModal';
import ParcaSecModal from '../components/ParcaSecModal';
import { useConfirmDialog } from '../contexts/ConfirmContext';
import {
  computeMusteriTahsilatToplam,
  requiresOdemeSorusu,
} from '../utils/musteriTahsilatHelper';

const safeText = (v) => sanitizeString(v ?? '');

const getErrorMessage = (err, fallback) => {
  if (!err) return fallback;
  return err?.data?.mesaj || err?.data?.message || err?.message || fallback;
};

const extractTechServiceId = (u) => {
  if (!u) return null;
  const ts = u.techService ?? u.techServiceId ?? u.teknikServisId;
  if (!ts) return null;
  if (typeof ts === 'string' && ts.trim()) return ts.trim();
  if (Array.isArray(ts) && ts.length > 0) {
    const first = ts[0];
    if (typeof first === 'string' && first.trim()) return first.trim();
    if (first && typeof first === 'object') {
      const id = first._id ?? first.id;
      return id ? String(id) : null;
    }
  }
  if (ts && typeof ts === 'object') {
    const id = ts._id ?? ts.id;
    return id ? String(id) : null;
  }
  return null;
};

const formatDate = (val) => {
  if (!val) return '—';
  try {
    const d = new Date(val);
    return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('tr-TR');
  } catch {
    return '—';
  }
};

const DURUM_LABELS = {
  'Randevu Tarihi Bekleniyor': { label: 'Tarih Bekleniyor', color: '#6b7280', bg: '#f3f4f6' },
  'İşlemde': { label: 'İşlemde', color: '#2563eb', bg: '#dbeafe' },
  'Randevu Tamamlandı': { label: 'Tamamlandı', color: '#059669', bg: '#d1fae5' },
  'Randevu İptal Edildi': { label: 'İptal', color: '#dc2626', bg: '#fee2e2' },
};

const RandevuIslemleriPage = () => {
  const { promptText } = useConfirmDialog();
  const { user, setUser } = useAuth();
  const goBack = useBackRoute('/');
  const techServiceId = useMemo(() => extractTechServiceId(user), [user]);
  const [profileRefetched, setProfileRefetched] = useState(false);

  const [technicians, setTechnicians] = useState([]);
  const [selectedTechnicianId, setSelectedTechnicianId] = useState('');
  const [appointments, setAppointments] = useState([]);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [loadingTech, setLoadingTech] = useState(false);
  const [loadingAppointments, setLoadingAppointments] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Tamamlama formu
  const [aciklama, setAciklama] = useState('');
  const [garantiDahilinde, setGarantiDahilinde] = useState(true);
  const [musteriyeFaturaEdilen, setMusteriyeFaturaEdilen] = useState('');
  const [odemeDurumu, setOdemeDurumu] = useState(null);
  const [odemeBeyanSonucu, setOdemeBeyanSonucu] = useState(null);
  const [beyanCozumuGerekli, setBeyanCozumuGerekli] = useState(false);

  // OTP modal
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otpValue, setOtpValue] = useState('');
  const [finishAction, setFinishAction] = useState(null); // 'bitir' | 'yeniRandevu'
  const [siparisGerekli, setSiparisGerekli] = useState(false);
  const [ilkCalistirmaYapildi, setIlkCalistirmaYapildi] = useState(true);
  const [submitError, setSubmitError] = useState('');

  // Yeni işlem/parça/ilave form
  const [newOperation, setNewOperation] = useState({ islemAdi: '', aciklama: '' });
  const [newPart, setNewPart] = useState({ parcaAdi: '', parcaKodu: '', adet: 1, fiyat: 0, aciklama: '' });
  const [newIlave, setNewIlave] = useState({ islemAdi: '', fiyat: 0, adet: 1, aciklama: '' });

  // Konum (haritadan seçim)
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [showMapModal, setShowMapModal] = useState(false);

  // Fotoğraf (dosya ekle)
  const [photoFiles, setPhotoFiles] = useState([]);
  const photoInputRef = React.useRef(null);

  // Parça seç modalı
  const [isParcaSecModalOpen, setIsParcaSecModalOpen] = useState(false);

  // Kurulum için cihaz ekleme
  const [showDeviceForm, setShowDeviceForm] = useState(false);
  const [deviceForm, setDeviceForm] = useState({
    seriNumarasi: '',
    icerikId: '',
    garantiBaslangicTarihi: '',
  });

  const loadTechnicians = useCallback(async () => {
    if (!techServiceId) return;
    setLoadingTech(true);
    setError('');
    try {
      const res = await fetchTechniciansByTechService(techServiceId, { aktif: true });
      const raw = res?.veri?.teknisyenler ?? res?.data?.teknisyenler ?? res?.veri;
      const list = Array.isArray(raw) ? raw : [];
      setTechnicians(list);
    } catch (e) {
      setError(getErrorMessage(e, 'Teknisyenler yüklenemedi'));
      setTechnicians([]);
    } finally {
      setLoadingTech(false);
    }
  }, [techServiceId]);

  // techServiceId yoksa ama kullanıcı tech-service rolündeyse profili yeniden çek
  useEffect(() => {
    if (!user || techServiceId || profileRefetched || !setUser) return;
    const rol = (user.rol ?? user.role ?? '').toLowerCase?.() || (user.rol ?? user.role ?? '');
    const isTech = rol === 'tech-service' || rol === 'teknikservis' || rol === 'techperson' || rol === 'teknisyen';
    if (!isTech) return;
    let cancelled = false;
    const refetch = async () => {
      try {
        const res = await fetchUserProfile();
        const updated = res?.veri?.kullanici ?? res?.veri ?? res?.kullanici ?? res;
        if (cancelled || !updated) return;
        if (extractTechServiceId(updated)) {
          setUser(updated);
        }
      } catch {
        /* sessiz */
      } finally {
        if (!cancelled) setProfileRefetched(true);
      }
    };
    refetch();
    return () => { cancelled = true; };
  }, [user, techServiceId, profileRefetched, setUser]);

  const loadAppointments = useCallback(async () => {
    if (!techServiceId) return;
    setLoadingAppointments(true);
    setError('');
    try {
      const params = { page: 1, limit: 100, aktif: true };
      if (selectedTechnicianId) params.teknisyenId = selectedTechnicianId;
      const res = await fetchAppointmentsByTechService(techServiceId, params);
      const list = res?.veri?.randevular ?? res?.veri?.data ?? res?.veri ?? [];
      setAppointments(Array.isArray(list) ? list : []);
    } catch (e) {
      setError(getErrorMessage(e, 'Randevular yüklenemedi'));
    } finally {
      setLoadingAppointments(false);
    }
  }, [techServiceId, selectedTechnicianId]);

  const loadAppointmentDetail = useCallback(async (apt) => {
    if (!apt) return;
    const id = apt.id ?? apt._id;
    if (!id) return;
    setLoadingDetail(true);
    setSubmitError('');
    setPhotoFiles([]);
    setLatitude('');
    setLongitude('');
    try {
      const res = await fetchAppointmentDetailed(id).catch(() => fetchAppointmentDetail(id));
      const veri = res?.veri ?? res;
      const randevu = veri?.randevu ?? veri;
      const servisKaydi = veri?.servisKaydi ?? randevu?.servisKaydi;
      const data = { ...veri, randevu: randevu ?? veri, servisKaydi };
      setSelectedAppointment(data);
      const aptData = randevu ?? veri;
      setAciklama(aptData?.aciklama ?? '');
      const tipLower = String(servisKaydi?.islemTipi ?? '').toLowerCase();
      if (tipLower.includes('kurulum')) {
        setGarantiDahilinde(null);
      } else if (tipLower.includes('bakım') || tipLower.includes('bakim')) {
        setGarantiDahilinde(false);
      } else {
        setGarantiDahilinde(aptData?.garantiDahilinde !== false);
      }
      setBeyanCozumuGerekli(!!aptData?.beyanCozumuGerekli);
      setOdemeDurumu(null);
      setOdemeBeyanSonucu(null);
      setMusteriyeFaturaEdilen(aptData?.musteriyeFaturaEdilen ?? '');
    } catch (e) {
      setSubmitError(getErrorMessage(e, 'Randevu detayı yüklenemedi'));
    } finally {
      setLoadingDetail(false);
    }
  }, []);

  useEffect(() => {
    if (techServiceId) {
      loadTechnicians();
    } else {
      setTechnicians([]);
    }
  }, [techServiceId, loadTechnicians]);

  // Teknisyenler API'den gelmezse randevulardan benzersiz teknisyenleri çıkar (yedek)
  const techniciansFromAppointments = useMemo(() => {
    if (technicians.length > 0) return [];
    const seen = new Set();
    const result = [];
    for (const apt of appointments) {
      const t = apt.teknisyen;
      if (!t) continue;
      const id = t.teknisyenId ?? t.id ?? t._id;
      if (!id || seen.has(id)) continue;
      seen.add(id);
      result.push({
        id,
        _id: id,
        ad: t.ad ?? t.adi ?? t.name ?? '',
        soyad: t.soyad ?? t.soyadi ?? t.surname ?? '',
      });
    }
    return result;
  }, [technicians.length, appointments]);

  const technicianList = technicians.length > 0 ? technicians : techniciansFromAppointments;

  useEffect(() => {
    loadAppointments();
  }, [loadAppointments]);

  const handleTechnicianChange = (e) => {
    setSelectedTechnicianId(e.target.value);
    setSelectedAppointment(null);
  };

  const handleSelectAppointment = (apt) => {
    setSelectedAppointment(null);
    loadAppointmentDetail(apt);
  };

  const getIdsFromAppointment = (apt) => {
    const data = apt?.randevu ?? apt;
    const srId = data?.servisKaydiId ?? apt?.servisKaydiId ?? apt?.servisKaydi?._id ?? apt?.servisKaydi?.id;
    const aptId = data?.id ?? data?._id ?? apt?.id ?? apt?._id;
    return { srId, aptId };
  };

  const handleIslemBasla = async () => {
    if (!selectedAppointment) return;
    const { srId, aptId } = getIdsFromAppointment(selectedAppointment);
    if (!srId || !aptId) return;
    setActionLoading(true);
    setSubmitError('');
    try {
      await updateAppointmentStatus(srId, aptId, 'İşlemde');
      await loadAppointmentDetail({ ...selectedAppointment, id: aptId });
      if (window.showNotification) window.showNotification('success', 'İşleme başlandı');
    } catch (e) {
      setSubmitError(getErrorMessage(e, 'Durum güncellenemedi'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddOperation = async () => {
    if (!selectedAppointment || !newOperation.islemAdi?.trim()) return;
    const { srId, aptId } = getIdsFromAppointment(selectedAppointment);
    if (!srId || !aptId) return;
    setActionLoading(true);
    setSubmitError('');
    try {
      await addAppointmentOperation(srId, aptId, {
        islemAdi: newOperation.islemAdi.trim(),
        aciklama: newOperation.aciklama?.trim() || undefined,
      });
      setNewOperation({ islemAdi: '', aciklama: '' });
      await loadAppointmentDetail({ ...selectedAppointment, id: aptId });
      if (window.showNotification) window.showNotification('success', 'Yapılan işlem eklendi');
    } catch (e) {
      setSubmitError(getErrorMessage(e, 'İşlem eklenemedi'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddPart = async () => {
    if (!selectedAppointment || !newPart.parcaAdi?.trim()) return;
    const { srId, aptId } = getIdsFromAppointment(selectedAppointment);
    if (!srId || !aptId) return;
    setActionLoading(true);
    setSubmitError('');
    try {
      await addAppointmentReplacedPart(srId, aptId, {
        parcaAdi: newPart.parcaAdi.trim(),
        parcaKodu: newPart.parcaKodu?.trim() || undefined,
        adet: parseAdet(newPart.adet, { min: 1 }),
        fiyat: Number(newPart.fiyat) || 0,
        aciklama: newPart.aciklama?.trim() || undefined,
      });
      setNewPart({ parcaAdi: '', parcaKodu: '', adet: 1, fiyat: 0, aciklama: '' });
      await loadAppointmentDetail({ ...selectedAppointment, id: aptId });
      if (window.showNotification) window.showNotification('success', 'Değiştirilen parça eklendi');
    } catch (e) {
      setSubmitError(getErrorMessage(e, 'Parça eklenemedi'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddIlaveIslem = async () => {
    if (!selectedAppointment || !newIlave.islemAdi?.trim()) return;
    const { aptId } = getIdsFromAppointment(selectedAppointment);
    if (!aptId) return;
    setActionLoading(true);
    setSubmitError('');
    try {
      await addAppointmentIlaveIslem(aptId, {
        islemler: [{
          islemAdi: newIlave.islemAdi.trim(),
          fiyat: Number(newIlave.fiyat) || 0,
          adet: parseAdet(newIlave.adet, { min: 1 }),
          aciklama: newIlave.aciklama?.trim() || undefined,
        }],
      });
      setNewIlave({ islemAdi: '', fiyat: 0, adet: 1, aciklama: '' });
      await loadAppointmentDetail({ ...selectedAppointment, id: aptId });
      if (window.showNotification) window.showNotification('success', 'İlave işlem eklendi');
    } catch (e) {
      setSubmitError(getErrorMessage(e, 'İlave işlem eklenemedi'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleParcaSecilenleriEkle = async (secilenler) => {
    if (!selectedAppointment || !secilenler?.length) return;
    const { srId, aptId } = getIdsFromAppointment(selectedAppointment);
    if (!srId || !aptId) return;
    setActionLoading(true);
    setSubmitError('');
    try {
      for (const p of secilenler) {
        await addAppointmentReplacedPart(srId, aptId, {
          parcaAdi: p.parcaAdi || '',
          parcaKodu: p.parcaKodu || undefined,
          adet: p.adet || 1,
          fiyat: Number(p.fiyat) || 0,
        });
      }
      await loadAppointmentDetail({ ...selectedAppointment, id: aptId });
      if (window.showNotification) window.showNotification('success', `${secilenler.length} parça eklendi`);
    } catch (e) {
      setSubmitError(getErrorMessage(e, 'Parçalar eklenemedi'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleMapSelect = ({ latitude: lat, longitude: lng }) => {
    setLatitude(String(lat));
    setLongitude(String(lng));
    setShowMapModal(false);
  };

  const openFinishModal = (action) => {
    const effectiveGaranti = isBakim ? false : garantiDahilinde;
    if (isKurulum && (garantiDahilinde !== true && garantiDahilinde !== false)) {
      setSubmitError('Kurulum için garanti dahilinde / harici seçimi zorunludur');
      return;
    }
    if (requiresOdemeSorusu(effectiveGaranti, tahsilatToplam, servisKaydi?.islemTipi) && !odemeDurumu) {
      setSubmitError('Garanti harici tahsilat için ödeme durumu seçilmelidir');
      return;
    }
    if (beyanCozumuGerekli && !odemeBeyanSonucu) {
      setSubmitError('Beyan edilen önceki borç için sonuç seçilmelidir');
      return;
    }
    if (odemeDurumu === 'alinamadi' && (!latitude || !longitude)) {
      setSubmitError('Ödeme alınamadı senaryosunda konum zorunludur');
      return;
    }
    setFinishAction(action);
    setOtpValue('');
    setShowOtpModal(true);
    setSubmitError('');
  };

  const buildFinishFormData = () => {
    const effectiveGaranti = isBakim ? false : garantiDahilinde;
    const formData = new FormData();
    formData.append('otp', otpValue?.trim() || '');
    const effectiveFinishAction = isBakim ? 'bitir' : finishAction;
    formData.append('sonrakiIslem', effectiveFinishAction === 'yeniRandevu' ? 'Yeni Randevu Oluşturulsun' : 'Servis Kaydını Bitir');
    formData.append('garantiDahilinde', String(effectiveGaranti));
    formData.append('siparisGerekli', String(isBakim ? false : siparisGerekli));
    if (isKurulum) {
      formData.append('ilkCalistirmaYapildi', String(ilkCalistirmaYapildi));
    }
    if (aciklama?.trim()) formData.append('notlar', aciklama.trim());
    if (musteriyeFaturaEdilen?.trim()) formData.append('musteriyeFaturaEdilen', musteriyeFaturaEdilen.trim());
    if (latitude) formData.append('latitude', latitude);
    if (longitude) formData.append('longitude', longitude);
    if (odemeDurumu) formData.append('odemeDurumu', odemeDurumu);
    if (odemeBeyanSonucu) formData.append('odemeBeyanSonucu', odemeBeyanSonucu);
    formData.append('yapilanIslemler', JSON.stringify(yapilanIslemler.map((o) => ({
      islemAdi: o.islemAdi ?? o.icerikAdi,
      islemKodu: o.islemKodu ?? o.icerikKodu,
      kategoriId: o.kategoriId,
      fiyat: o.fiyat ?? 0,
      adet: o.adet ?? 1,
    }))));
    formData.append('degistirilenParcalar', JSON.stringify(degistirilenParcalar.map((p) => ({
      parcaAdi: p.parcaAdi ?? p.icerikAdi,
      parcaKodu: p.parcaKodu ?? p.icerikKodu,
      adet: p.adet ?? 1,
      fiyat: p.fiyat ?? 0,
    }))));
    formData.append('ilaveIslemler', JSON.stringify(ilaveIslemler.map((i) => ({
      islemAdi: i.islemAdi,
      fiyat: i.fiyat ?? 0,
      adet: i.adet ?? 1,
      aciklama: i.aciklama || '',
    }))));
    photoFiles.forEach((file) => formData.append('photos', file));
    return formData;
  };

  const handleFinishSubmit = async () => {
    if (!selectedAppointment) return;
    const { aptId } = getIdsFromAppointment(selectedAppointment);
    if (!aptId) {
      setSubmitError('Randevu ID bulunamadı');
      return;
    }
    setActionLoading(true);
    setSubmitError('');
    try {
      // Önce OTP oluştur (mobile akışı) - yoksa direkt finish kullan
      let otpGenerated = false;
      try {
        await generateOtp(aptId);
        otpGenerated = true;
      } catch {
        /* Mobile endpoint yoksa eski akışa geç */
      }

      if (otpGenerated) {
        const formData = buildFinishFormData();
        const useDeferred = odemeDurumu === 'alinamadi' && !otpValue?.trim();
        if (useDeferred) {
          formData.append('odemeAlinamadiTamamlama', 'true');
          formData.append('otpAtlandi', 'true');
          await completeDeferred(aptId, formData);
        } else {
          await verifyOtp(aptId, formData);
        }
      } else {
        const { srId } = getIdsFromAppointment(selectedAppointment);
        if (!srId) throw new Error('Servis kaydı ID bulunamadı');
        const payload = {
          garantiDahilinde: isBakim ? false : garantiDahilinde,
          musteriyeFaturaEdilen: musteriyeFaturaEdilen?.trim() || undefined,
          aciklama: aciklama?.trim() || undefined,
          durum: 'Randevu Tamamlandı',
        };
        if (otpValue?.trim()) payload.otp = otpValue.trim();
        if (!isBakim && finishAction === 'yeniRandevu') payload.yeniRandevuGerekli = true;
        if (!isBakim && finishAction === 'yeniRandevu') payload.siparisGerekli = siparisGerekli;
        await finishAppointment(srId, aptId, payload);
      }
      setShowOtpModal(false);
      setFinishAction(null);
      setOtpValue('');
      setSiparisGerekli(false);
      setIlkCalistirmaYapildi(true);
      setPhotoFiles([]);
      setLatitude('');
      setLongitude('');
      await loadAppointmentDetail({ ...selectedAppointment, id: aptId });
      if (window.showNotification) window.showNotification('success', 'Randevu tamamlandı');
    } catch (e) {
      setSubmitError(getErrorMessage(e, 'Tamamlama başarısız'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddDevice = async () => {
    if (!musteriId || !deviceForm.seriNumarasi?.trim()) {
      setSubmitError('Müşteri ve seri numarası zorunludur');
      return;
    }
    setActionLoading(true);
    setSubmitError('');
    try {
      await createDevice({
        musteriId,
        seriNumarasi: deviceForm.seriNumarasi.trim(),
        icerikId: deviceForm.icerikId?.trim() || undefined,
        urunId: deviceForm.icerikId?.trim() || undefined,
        garantiBaslangicTarihi: deviceForm.garantiBaslangicTarihi || new Date().toISOString().split('T')[0],
      });
      setDeviceForm({ seriNumarasi: '', icerikId: '', garantiBaslangicTarihi: '' });
      setShowDeviceForm(false);
      if (window.showNotification) window.showNotification('success', 'Cihaz eklendi');
    } catch (e) {
      setSubmitError(getErrorMessage(e, 'Cihaz eklenemedi'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelAppointment = async () => {
    if (!selectedAppointment) return;
    const reason = await promptText({
      title: 'Randevu iptali',
      message: 'İptal nedeni (en az 10 karakter):',
      placeholder: 'İptal nedeni',
      minLength: 10,
      multiline: true,
    });
    if (reason === null) return;
    const { srId, aptId } = getIdsFromAppointment(selectedAppointment);
    if (!srId || !aptId) return;
    setActionLoading(true);
    setSubmitError('');
    try {
      await cancelAppointment(srId, aptId, reason.trim(), 'tech-service');
      await loadAppointmentDetail({ ...selectedAppointment, id: aptId });
      if (window.showNotification) window.showNotification('success', 'Randevu iptal edildi');
    } catch (e) {
      setSubmitError(getErrorMessage(e, 'İptal başarısız'));
    } finally {
      setActionLoading(false);
    }
  };

  const apt = selectedAppointment;
  const aptData = apt?.randevu ?? apt;
  const servisKaydi = apt?.servisKaydi ?? {};
  const islemTipi = String(servisKaydi?.islemTipi ?? '').toLowerCase();
  const isKurulum = islemTipi.includes('kurulum');
  const isBakim = islemTipi.includes('bakım') || islemTipi.includes('bakim');
  const bakimFiyatOnizleme = aptData?.bakimFiyatOnizleme ?? null;
  const musteriId = servisKaydi?.musteriId ?? apt?.musteriId ?? servisKaydi?.musteri?._id ?? servisKaydi?.musteri?.id;
  const durum = aptData?.durum ?? '';
  const isIslemde = durum === 'İşlemde';
  const isTamamlandi = durum === 'Randevu Tamamlandı';

  const yapilanIslemler = aptData?.yapilanIslemler ?? [];
  const degistirilenParcalar = aptData?.degistirilenParcalar ?? [];
  const ilaveIslemler = aptData?.ilaveIslemler ?? [];

  const effectiveGaranti = isBakim ? false : garantiDahilinde;
  const tahsilatToplam = useMemo(() => {
    const pseudoRandevu = {
      garantiDahilinde: effectiveGaranti,
      yapilanIslemler,
      ilaveIslemler,
      bakimOzeti: bakimFiyatOnizleme,
    };
    return computeMusteriTahsilatToplam(pseudoRandevu, servisKaydi?.islemTipi, bakimFiyatOnizleme);
  }, [effectiveGaranti, yapilanIslemler, ilaveIslemler, bakimFiyatOnizleme, servisKaydi?.islemTipi]);
  const odemeSorusuGerekli = requiresOdemeSorusu(effectiveGaranti, tahsilatToplam, servisKaydi?.islemTipi);

  if (!techServiceId) {
    return (
      <div className="main-content-inner" style={{ padding: '24px' }}>
        <p style={{ color: '#dc2626' }}>Teknik servis bilgisi bulunamadı.</p>
      </div>
    );
  }

  return (
    <div className="main-content-inner" style={{ padding: '24px', maxWidth: '900px' }}>
      <button
        type="button"
        className="secondary-btn"
        onClick={goBack}
        style={{ marginBottom: '16px' }}
      >
        <i className="fas fa-arrow-left"></i>
        <span>Geri Dön</span>
      </button>
      <h1 style={{ marginBottom: '24px', fontSize: '1.5rem' }}>
        <i className="fas fa-mobile-alt" style={{ marginRight: '8px' }}></i>
        Randevu İşlemleri
      </h1>
      <p style={{ color: '#64748b', marginBottom: '24px' }}>
        Mobil uygulama akışını web üzerinden test edin. Konum haritadan seçilir, fotoğraf dosya ekle ile yüklenir.
        Önce teknisyen seçin, sonra randevu üzerinde işlem yapın.
      </p>

      {error && (
        <div style={{ padding: '12px 16px', background: '#fef2f2', borderRadius: '8px', marginBottom: '16px', color: '#dc2626' }}>
          {error}
        </div>
      )}

      {/* 1. Teknisyen Seçimi */}
      <div style={{ marginBottom: '24px' }}>
        <label style={{ display: 'block', fontWeight: 600, marginBottom: '8px' }}>Teknisyen Seçin</label>
        <select
          value={selectedTechnicianId}
          onChange={handleTechnicianChange}
          disabled={loadingTech}
          style={{ minWidth: '280px', padding: '10px 12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}
        >
          <option value="">Tüm teknisyenler</option>
          {technicianList.map((t) => (
            <option key={t.id || t._id} value={t.id || t._id}>
              {safeText(t.ad ?? t.adi)} {safeText(t.soyad ?? t.soyadi)}
            </option>
          ))}
        </select>
      </div>

      {/* 2. Randevu Listesi */}
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ fontSize: '1.1rem', marginBottom: '12px' }}>Randevular</h2>
        {loadingAppointments ? (
          <p>Yükleniyor...</p>
        ) : appointments.length === 0 ? (
          <p style={{ color: '#64748b' }}>Randevu bulunamadı.</p>
        ) : (
          <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
            <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '8px' }}>
              <i className="fas fa-mouse-pointer" style={{ marginRight: '6px' }}></i>
              Randevu detayına gitmek için satıra veya "Seç" butonuna tıklayın
            </p>
            <table className="admins-table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>Randevu No</th>
                  <th>Müşteri</th>
                  <th>Tarih</th>
                  <th>Durum</th>
                  <th className="islemler-sutunu" style={{ width: '120px' }}>İşlem</th>
                </tr>
              </thead>
              <tbody>
                {appointments.map((a) => {
                  const sr = a.servisKaydi ?? a.servisKaydiId ?? {};
                  const musteri = sr.musteri ?? {};
                  const musteriAd = `${musteri.adi ?? musteri.name ?? ''} ${musteri.soyadi ?? musteri.surname ?? ''}`.trim() || '—';
                  const ds = DURUM_LABELS[a.durum] ?? { label: a.durum ?? '—', color: '#6b7280', bg: '#f3f4f6' };
                  return (
                    <tr
                      key={a.id || a._id}
                      onClick={() => handleSelectAppointment(a)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => e.key === 'Enter' && handleSelectAppointment(a)}
                      style={{ cursor: 'pointer' }}
                      className="admins-table-clickable-row"
                    >
                      <td>{safeText(a.randevuNumarasi)}</td>
                      <td>{musteriAd}</td>
                      <td>{formatDate(a.randevuTarihi)} {a.randevuSaati || ''}</td>
                      <td>
                        <span style={{ padding: '4px 8px', borderRadius: '6px', fontSize: '12px', background: ds.bg, color: ds.color }}>
                          {ds.label}
                        </span>
                      </td>
                      <td className="islemler-sutunu">
                        <button
                          type="button"
                          className="category-action-btn edit-btn"
                          onClick={(e) => { e.stopPropagation(); handleSelectAppointment(a); }}
                        >
                          <i className="fas fa-chevron-right" style={{ marginRight: '4px' }}></i>
                          Seç
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* 3. Randevu Detay - Mobil Akış */}
      {loadingDetail ? (
        <p>Randevu detayı yükleniyor...</p>
      ) : apt ? (
        <div style={{ border: '1px solid #e2e8f0', borderRadius: '12px', padding: '24px', background: '#fff' }}>
          <h2 style={{ marginBottom: '16px' }}>Randevu Detayı – {safeText(aptData?.randevuNumarasi)}</h2>
          {submitError && (
            <div style={{ padding: '12px', background: '#fef2f2', borderRadius: '8px', marginBottom: '16px', color: '#dc2626' }}>
              {submitError}
            </div>
          )}

          {/* İşleme Başla */}
          {!isIslemde && !isTamamlandi && (
            <div style={{ marginBottom: '20px' }}>
              <button
                type="button"
                className="category-action-btn edit-btn"
                onClick={handleIslemBasla}
                disabled={actionLoading}
              >
                <i className="fas fa-play" style={{ marginRight: '6px' }}></i>
                İşleme Başla
              </button>
            </div>
          )}

          {/* Yapılan İşlemler */}
          {isIslemde && (
            <>
              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ marginBottom: '8px' }}>Yapılan İşlemler</h3>
                {yapilanIslemler.length > 0 && (
                  <ul style={{ marginBottom: '12px', paddingLeft: '20px' }}>
                    {yapilanIslemler.map((op, i) => (
                      <li key={i}>{safeText(op.islemAdi)} {op.aciklama && `– ${op.aciklama}`}</li>
                    ))}
                  </ul>
                )}
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <input
                    type="text"
                    placeholder="İşlem adı"
                    value={newOperation.islemAdi}
                    onChange={(e) => setNewOperation((o) => ({ ...o, islemAdi: e.target.value }))}
                    style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #e2e8f0', minWidth: '180px' }}
                  />
                  <input
                    type="text"
                    placeholder="Açıklama (ops.)"
                    value={newOperation.aciklama}
                    onChange={(e) => setNewOperation((o) => ({ ...o, aciklama: e.target.value }))}
                    style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #e2e8f0', minWidth: '180px' }}
                  />
                  <button type="button" className="category-action-btn edit-btn" onClick={handleAddOperation} disabled={actionLoading}>
                    Ekle
                  </button>
                </div>
              </div>

              {/* Değiştirilen Parçalar */}
              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ marginBottom: '8px' }}>Değiştirilen Parçalar</h3>
                {degistirilenParcalar.length > 0 && (
                  <ul style={{ marginBottom: '12px', paddingLeft: '20px' }}>
                    {degistirilenParcalar.map((p, i) => (
                      <li key={i}>{safeText(p.parcaAdi)} – Adet: {p.adet || 1} {p.fiyat != null && `, Fiyat: ${p.fiyat} ₺`}</li>
                    ))}
                  </ul>
                )}
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: '8px' }}>
                  <button
                    type="button"
                    className="secondary-btn"
                    onClick={() => setIsParcaSecModalOpen(true)}
                    style={{ padding: '8px 14px' }}
                  >
                    <i className="fas fa-search" style={{ marginRight: '6px' }}></i>
                    Parçadan Seç
                  </button>
                </div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <input
                    type="text"
                    placeholder="Parça adı"
                    value={newPart.parcaAdi}
                    onChange={(e) => setNewPart((p) => ({ ...p, parcaAdi: e.target.value }))}
                    style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #e2e8f0', minWidth: '120px' }}
                  />
                  <input
                    type="text"
                    placeholder="Kod"
                    value={newPart.parcaKodu}
                    onChange={(e) => setNewPart((p) => ({ ...p, parcaKodu: e.target.value }))}
                    style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #e2e8f0', width: '80px' }}
                  />
                  <IntegerInput
                    min={1}
                    placeholder="Adet"
                    value={newPart.adet}
                    onChange={(v) => setNewPart((p) => ({ ...p, adet: v }))}
                    style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #e2e8f0', width: '70px' }}
                  />
                  <input
                    type="number"
                    min={0}
                    step="0.01"
                    placeholder="Fiyat"
                    value={newPart.fiyat}
                    onChange={(e) => setNewPart((p) => ({ ...p, fiyat: e.target.value }))}
                    style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #e2e8f0', width: '90px' }}
                  />
                  <button type="button" className="category-action-btn edit-btn" onClick={handleAddPart} disabled={actionLoading}>
                    Parça Ekle
                  </button>
                </div>
              </div>

              {/* İlave İşlemler */}
              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ marginBottom: '8px' }}>İlave İşlemler</h3>
                {ilaveIslemler.length > 0 && (
                  <ul style={{ marginBottom: '12px', paddingLeft: '20px' }}>
                    {ilaveIslemler.map((il, i) => (
                      <li key={i}>{safeText(il.islemAdi)} – {il.adet || 1} adet, {il.fiyat ?? 0} ₺</li>
                    ))}
                  </ul>
                )}
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <input
                    type="text"
                    placeholder="İlave işlem adı"
                    value={newIlave.islemAdi}
                    onChange={(e) => setNewIlave((p) => ({ ...p, islemAdi: e.target.value }))}
                    style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #e2e8f0', minWidth: '140px' }}
                  />
                  <input
                    type="number"
                    min={0}
                    step="0.01"
                    placeholder="Fiyat"
                    value={newIlave.fiyat}
                    onChange={(e) => setNewIlave((p) => ({ ...p, fiyat: e.target.value }))}
                    style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #e2e8f0', width: '90px' }}
                  />
                  <IntegerInput
                    min={1}
                    placeholder="Adet"
                    value={newIlave.adet}
                    onChange={(v) => setNewIlave((p) => ({ ...p, adet: v }))}
                    style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #e2e8f0', width: '70px' }}
                  />
                  <button type="button" className="category-action-btn edit-btn" onClick={handleAddIlaveIslem} disabled={actionLoading}>
                    İlave Ekle
                  </button>
                </div>
              </div>

              {/* Kurulum: Cihaz Ekle */}
              {isKurulum && (
                <div style={{ marginBottom: '20px' }}>
                  <h3 style={{ marginBottom: '8px' }}>Cihaz Ekle (Kurulum)</h3>
                  {!showDeviceForm ? (
                    <button
                      type="button"
                      className="category-action-btn edit-btn"
                      onClick={() => setShowDeviceForm(true)}
                    >
                      <i className="fas fa-plus" style={{ marginRight: '6px' }}></i>
                      Cihaz Ekle
                    </button>
                  ) : (
                    <div style={{ padding: '16px', background: '#f8fafc', borderRadius: '8px', marginTop: '8px' }}>
                      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
                        <input
                          type="text"
                          placeholder="Seri numarası *"
                          value={deviceForm.seriNumarasi}
                          onChange={(e) => setDeviceForm((d) => ({ ...d, seriNumarasi: e.target.value }))}
                          style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #e2e8f0', minWidth: '140px' }}
                        />
                        <input
                          type="text"
                          placeholder="Ürün/İçerik ID"
                          value={deviceForm.icerikId}
                          onChange={(e) => setDeviceForm((d) => ({ ...d, icerikId: e.target.value }))}
                          style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #e2e8f0', minWidth: '140px' }}
                        />
                        <input
                          type="date"
                          value={deviceForm.garantiBaslangicTarihi}
                          onChange={(e) => setDeviceForm((d) => ({ ...d, garantiBaslangicTarihi: e.target.value }))}
                          style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                        />
                      </div>
                      <p style={{ fontSize: '12px', color: '#64748b', margin: '0 0 12px' }}>
                        Kurulum garanti süresi (ay) seçilen ürün içeriğinden uygulanır; tanımda yoksa sunucu varsayılanı kullanılır.
                      </p>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button type="button" className="category-action-btn edit-btn" onClick={handleAddDevice} disabled={actionLoading}>
                          Kaydet
                        </button>
                        <button type="button" className="category-action-btn" onClick={() => setShowDeviceForm(false)}>
                          İptal
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Randevuyu Tamamla */}
              <div style={{ marginTop: '24px', paddingTop: '20px', borderTop: '1px solid #e2e8f0' }}>
                <h3 style={{ marginBottom: '12px' }}>Randevuyu Tamamla</h3>
                {isBakim && (
                  <div
                    style={{
                      marginBottom: '16px',
                      padding: '14px 16px',
                      borderRadius: '10px',
                      background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                      border: '1px solid #bfdbfe',
                    }}
                  >
                    <div style={{ fontWeight: 600, color: '#1e40af', marginBottom: '4px' }}>
                      Bakım bedeli (müşteri tahsilatı)
                    </div>
                    {bakimFiyatOnizleme?.eksikUrunler?.length ? (
                      <div style={{ fontSize: '14px', color: '#b45309' }}>
                        Eksik fiyat tanımı: {bakimFiyatOnizleme.eksikUrunler.join(', ')}
                      </div>
                    ) : bakimFiyatOnizleme?.bakimFiyatiToplam != null ? (
                      <div style={{ fontSize: '20px', fontWeight: 700, color: '#1e3a8a' }}>
                        {Number(bakimFiyatOnizleme.bakimFiyatiToplam).toLocaleString('tr-TR', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}{' '}
                        ₺
                      </div>
                    ) : null}
                  </div>
                )}
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>Randevu notu</label>
                  <textarea
                    value={aciklama}
                    onChange={(e) => setAciklama(e.target.value)}
                    placeholder="Not yazın..."
                    rows={2}
                    style={{ width: '100%', maxWidth: '400px', padding: '8px 12px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                  />
                </div>


                {isKurulum && (
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ fontWeight: 500, marginBottom: '8px' }}>Garanti kapsamı</div>
                    <div style={{ display: 'flex', gap: '16px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                        <input type="radio" name="garanti-kurulum" checked={garantiDahilinde === true} onChange={() => setGarantiDahilinde(true)} />
                        Garanti dahilinde
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                        <input type="radio" name="garanti-kurulum" checked={garantiDahilinde === false} onChange={() => setGarantiDahilinde(false)} />
                        Garanti harici
                      </label>
                    </div>
                  </div>
                )}

                {!isKurulum && !isBakim && (
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={garantiDahilinde === true}
                      onChange={(e) => setGarantiDahilinde(e.target.checked)}
                    />
                    Garanti dahilinde
                  </label>
                </div>
                )}

                {(odemeSorusuGerekli || tahsilatToplam > 0) && (
                  <div style={{ marginBottom: '16px', padding: '14px', borderRadius: '10px', background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                    <div style={{ fontWeight: 600, marginBottom: '6px' }}>Müşteri tahsilat özeti</div>
                    <div style={{ fontSize: '18px', fontWeight: 700, color: '#1e3a8a', marginBottom: '10px' }}>
                      {Number(tahsilatToplam).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                    </div>
                    {odemeSorusuGerekli && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div style={{ fontWeight: 500, fontSize: '14px' }}>Ödeme alındı mı?</div>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                          <input type="radio" name="odeme-durumu" checked={odemeDurumu === 'alindi'} onChange={() => setOdemeDurumu('alindi')} />
                          Ödeme yapıldı
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                          <input type="radio" name="odeme-durumu" checked={odemeDurumu === 'alinamadi'} onChange={() => setOdemeDurumu('alinamadi')} />
                          Ödeme yapılmadı (OTP opsiyonel, konum zorunlu)
                        </label>
                      </div>
                    )}
                  </div>
                )}

                {beyanCozumuGerekli && (
                  <div style={{ marginBottom: '16px', padding: '14px', borderRadius: '10px', background: '#fffbeb', border: '1px solid #fcd34d' }}>
                    <div style={{ fontWeight: 600, marginBottom: '8px', color: '#92400e' }}>Beyan edilen önceki borç alındı mı?</div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginBottom: '6px' }}>
                      <input type="radio" name="beyan-sonuc" checked={odemeBeyanSonucu === 'alindi'} onChange={() => setOdemeBeyanSonucu('alindi')} />
                      Evet, alındı
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input type="radio" name="beyan-sonuc" checked={odemeBeyanSonucu === 'alinamadi'} onChange={() => setOdemeBeyanSonucu('alinamadi')} />
                      Hayır, alınamadı
                    </label>
                  </div>
                )}

                {!garantiDahilinde && !isBakim && (
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>Müşteriye fatura edilen</label>
                    <input
                      type="text"
                      value={musteriyeFaturaEdilen}
                      onChange={(e) => setMusteriyeFaturaEdilen(e.target.value)}
                      placeholder="Örn: 150.00 TL"
                      style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #e2e8f0', minWidth: '200px' }}
                    />
                  </div>
                )}
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>Konum</label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <input
                      type="number"
                      step="any"
                      placeholder="Enlem"
                      value={latitude}
                      onChange={(e) => setLatitude(e.target.value)}
                      style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #e2e8f0', width: '120px' }}
                    />
                    <input
                      type="number"
                      step="any"
                      placeholder="Boylam"
                      value={longitude}
                      onChange={(e) => setLongitude(e.target.value)}
                      style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #e2e8f0', width: '120px' }}
                    />
                    <button type="button" className="category-action-btn" onClick={() => setShowMapModal(true)}>
                      <i className="fas fa-map-marker-alt" style={{ marginRight: '6px' }}></i>
                      Haritadan Seç
                    </button>
                  </div>
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>Fotoğraf (Dosya Ekle)</label>
                  <input
                    ref={photoInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      setPhotoFiles((prev) => [...prev, ...files].slice(0, 20));
                    }}
                    style={{ display: 'none' }}
                  />
                  <button
                    type="button"
                    className="category-action-btn"
                    onClick={() => photoInputRef.current?.click()}
                  >
                    <i className="fas fa-paperclip" style={{ marginRight: '6px' }}></i>
                    Dosya Ekle
                  </button>
                  {photoFiles.length > 0 && (
                    <span style={{ marginLeft: '12px', fontSize: '13px', color: '#64748b' }}>
                      {photoFiles.length} dosya seçildi
                      <button
                        type="button"
                        onClick={() => { setPhotoFiles([]); photoInputRef.current && (photoInputRef.current.value = ''); }}
                        style={{ marginLeft: '8px', color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px' }}
                      >
                        Temizle
                      </button>
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '12px', marginTop: '16px', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    className="category-action-btn edit-btn"
                    onClick={() => openFinishModal('bitir')}
                    style={{ background: '#059669', color: '#fff', border: 'none' }}
                  >
                    {isBakim ? 'Bakımı Tamamla (OTP)' : 'Servis Kaydını Bitir'}
                  </button>
                  {!isBakim && (
                  <button
                    type="button"
                    className="category-action-btn edit-btn"
                    onClick={() => openFinishModal('yeniRandevu')}
                    style={{ background: '#2563eb', color: '#fff', border: 'none' }}
                  >
                    Yeni Randevu Gerekli
                  </button>
                  )}
                  <button
                    type="button"
                    className="category-action-btn"
                    onClick={handleCancelAppointment}
                    style={{ background: '#ef4444', color: '#fff', border: 'none' }}
                  >
                    Randevuyu İptal Et
                  </button>
                </div>
              </div>
            </>
          )}

          {isTamamlandi && (
            <p style={{ color: '#059669', fontWeight: 500 }}>
              <i className="fas fa-check-circle" style={{ marginRight: '8px' }}></i>
              Bu randevu tamamlanmıştır.
            </p>
          )}
        </div>
      ) : null}

      {/* Parça Seç Modal - kategoriler, checkbox, fotoğraf / no-image ikonu + tooltip */}
      {apt && (
        <ParcaSecModal
          isOpen={isParcaSecModalOpen}
          onClose={() => setIsParcaSecModalOpen(false)}
          servisKaydiId={getIdsFromAppointment(apt).srId}
          onSeçilenleriEkle={handleParcaSecilenleriEkle}
        />
      )}

      {/* Harita Modal */}
      <MapPickerModal
        isOpen={showMapModal}
        onClose={() => setShowMapModal(false)}
        onSelect={handleMapSelect}
        initialLat={latitude ? parseFloat(latitude) : undefined}
        initialLng={longitude ? parseFloat(longitude) : undefined}
      />

      {/* OTP Modal */}
      {showOtpModal && (
        <div
          className="modal-overlay"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
          onClick={(e) => e.target === e.currentTarget && setShowOtpModal(false)}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: '12px',
              padding: '24px',
              minWidth: '320px',
              maxWidth: '90%',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginBottom: '16px' }}>OTP Doğrulama</h3>
            <p style={{ color: '#64748b', marginBottom: '16px', fontSize: '14px' }}>
              {odemeDurumu === 'alinamadi'
                ? 'Ödeme alınamadı senaryosunda OTP opsiyoneldir; boş bırakıp tamamlayabilirsiniz.'
                : 'Müşteriye gönderilen OTP kodunu girin (geliştirme için boş bırakılabilir)'}
            </p>
            <input
              type="text"
              placeholder="OTP kodu"
              value={otpValue}
              onChange={(e) => setOtpValue(e.target.value)}
              maxLength={6}
              style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '16px' }}
            />
            {finishAction === 'yeniRandevu' && (
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={siparisGerekli}
                    onChange={(e) => setSiparisGerekli(e.target.checked)}
                  />
                  Sipariş Gerekli
                </label>
              </div>
            )}
            {isKurulum && (
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={ilkCalistirmaYapildi}
                    onChange={(e) => setIlkCalistirmaYapildi(e.target.checked)}
                  />
                  İlk çalıştırma yapıldı
                </label>
              </div>
            )}
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="category-action-btn"
                onClick={() => { setShowOtpModal(false); setFinishAction(null); setSiparisGerekli(false); setIlkCalistirmaYapildi(true); }}
              >
                İptal
              </button>
              <button
                type="button"
                className="category-action-btn edit-btn"
                onClick={handleFinishSubmit}
                disabled={actionLoading}
              >
                {actionLoading ? 'Gönderiliyor...' : 'Onayla'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RandevuIslemleriPage;
